import { z } from 'zod'

import { codeParamSchema, lenientList, moneySchema } from '@/schemas/common'
import { pageSizeSchema, tablePageSchema, type TablePage } from '@/schemas/table'

/**
 * Broker Chirfaar — `/api/v5/brokers/`.
 *
 * "Chirfaar" is breaking a trade down: the same floorsheet the Market slice
 * already reads, turned into *who traded what*. Twelve surfaces sit on fifteen
 * endpoints, so the shapes below are grouped by the payload they describe
 * rather than by the screen that draws them — Compare and By broker both read
 * `BrokerStockFlow`, and two screens reading one shape is the point.
 *
 * Two rules the whole file turns on:
 *
 *   1. A broker number is a STRING. NEPSE writes "07", and a number prints as
 *      7 in the one column where the leading zero is the identifier. The same
 *      rule as src/schemas/floorsheet.ts, for the same reason.
 *
 *   2. A broker is a trading house, not a person. Heavy buying through one
 *      broker is many clients at once. Nothing here is named as if a broker
 *      held a view.
 */

// ── The window every read is taken over ──────────────────────────────────────

/**
 * The Today / 1W / 1M / 3M toggle.
 *
 * Counted in TRADING days off the real `nepse_trade_date` calendar (1 / 5 / 22
 * / 66 sessions), never in calendar days — legacy v4 subtracted calendar days
 * and mis-counted across weekends and holidays. Nothing client-side may
 * compute a date range from these.
 */
export const BROKER_PERIODS = ['today', '1w', '1m', '3m'] as const
export const brokerPeriodSchema = z.enum(BROKER_PERIODS)

/**
 * Where the answer came from.
 *
 * `precomputed` is the nightly summary; `live` is the backend aggregating raw
 * floorsheet rows because the summary has not caught up. The live fallback can
 * only cover the LATEST SESSION, so a `1m` request that answers `live` returned
 * one day, not a month.
 *
 * `floorsheet` is the third value, and it is NOT the same thing as `live`.
 * PV range (`services/broker/pvrange.py`) has no summary table to fall back
 * from — it reads `chirfaar_floor_sheet` directly for every session in the
 * window ("no precompute involved", its own docstring) — so a `3m` answer
 * really does cover three months of raw trades. Widening this enum rather
 * than giving PV range a parallel window type is the one-`z.enum`-per-concept
 * rule: `isPartialWindow` below keys on `live` specifically, so a
 * `floorsheet` window correctly reports itself as whole, and `WindowNote`
 * needs no second code path. The alternative — reusing `live` for it — would
 * make every PV range window over a multi-session period caption itself
 * "today's session only", which is the exact false statement this triple
 * exists to prevent, in reverse.
 */
export const BROKER_SOURCES = ['precomputed', 'live', 'floorsheet'] as const
export const brokerSourceSchema = z.enum(BROKER_SOURCES)

/**
 * The period / as_of / source triple that every windowed payload carries,
 * lifted out of each one so a screen can render the freshness note once.
 *
 * It is never dropped in mapping. A screen that quietly prints "1M" over a
 * single session's numbers is the exact failure this exists to prevent.
 */
export const dataWindowSchema = z.object({
  period: brokerPeriodSchema,
  /** ISO date (`2026-09-16`) of the newest session covered; null before the market has ever traded. */
  asOf: z.string().min(1).nullable(),
  source: brokerSourceSchema,
})

/**
 * True when the answer covers less than the period asked for.
 *
 * The one case the backend can produce: a multi-session period served from the
 * live fallback, which only ever aggregates the latest session. Screens use
 * this to caption the number ("today only — the daily summary is still
 * catching up"), never to hide it.
 */
export const isPartialWindow = (window: DataWindow): boolean =>
  window.source === 'live' && window.period !== 'today'

/**
 * As much of the window as a PAGED list can carry: `period` and `asOf`, never
 * `source`.
 *
 * A windowed OBJECT payload (`BrokerDetail`, `Compare`, `SectorFlow`, …) has a
 * body to put the full triple in. A paged list's body is the rows, so anything
 * about the window has to travel in the envelope's `meta` — and only ONE list
 * endpoint merges any of it in: `GET /api/v5/brokers/busy-names/` adds
 * `as_of` and `period` (`services/broker/busynames.py:52`). It does not add
 * `source`, so the triple cannot be completed here, and this is deliberately a
 * DIFFERENT type rather than a `DataWindow` with a `source` invented for it:
 * fabricating `precomputed` would make `isPartialWindow` answer `false` for a
 * window nobody actually checked, which is the exact failure `DataWindow`
 * exists to prevent. See the report's backend section — `source` in that meta
 * is a one-line backend change, and `listWindowSchema` becomes `dataWindowSchema`
 * the day it lands.
 *
 * Built by `.pick()` off `dataWindowSchema` rather than restated, so the two
 * cannot drift: `period` stays the trading-day enum and `asOf` stays nullable.
 */
export const listWindowSchema = dataWindowSchema.pick({ period: true, asOf: true })

/**
 * One page of a server-paged list that also reports the window it was taken
 * over.
 *
 * `tablePageSchema` (src/schemas/table.ts) is shared with floorsheet, movers,
 * market events, prices and depth, none of which has a window, so the window
 * is added HERE — in the broker domain's own vocabulary — instead of on the
 * shared envelope. Prices and depth already extend the same factory the same
 * way (`liveMarketPageSchema`, `depthPageSchema`), so this is the established
 * shape for "the shared page, plus what this endpoint alone carries".
 *
 * `window` is NULLABLE, not optional and not required. Required would mean a
 * backend that stopped merging the window into `meta` blanks the whole table
 * with a parse error, and a missing freshness caption must never cost a member
 * their rows. Null says plainly "this page does not know its window", which is
 * also the honest answer for the three paged broker endpoints that never send
 * one.
 */
export const windowedTablePageSchema = <Row extends z.ZodType>(rowSchema: Row) =>
  tablePageSchema(rowSchema).extend({ window: listWindowSchema.nullable() })

export interface WindowedTablePage<Row> extends TablePage<Row> {
  window: ListWindow | null
}

// ── Categorical values the backend spells out ────────────────────────────────

/** Which way a flow leaned. `services/broker/measures.py`: net > 0, net < 0, net == 0. */
export const brokerLeanSchema = z.enum(['bought', 'sold', 'balanced'])

/**
 * The same idea in the large-trades leaderboard, in the backend's OTHER
 * vocabulary — `buying` / `selling`, not `bought` / `sold`
 * (`services/broker/types.py:359` against `:28`). Two enums rather than one
 * union, so a component cannot be handed the wrong wording and render it.
 */
export const largeTradeLeanSchema = z.enum(['buying', 'selling', 'balanced'])

/** Whether the broker being viewed was the buyer or the seller on a contract. */
export const brokerTradeSideSchema = z.enum(['buy', 'sell'])

/** The "trades over…" floor: 5 lakh, 10 lakh, 25 lakh, 50 lakh, 1 crore. */
export const LARGE_TRADE_SIZES = ['5l', '10l', '25l', '50l', '1cr'] as const
export const largeTradeSizeSchema = z.enum(LARGE_TRADE_SIZES)

/** Where a large trade sits relative to the selected floor. 50 L is the step. */
export const largeTradeBandSchema = z.enum(['very_large', 'large'])

/**
 * How All brokers may be ordered. Closed, and the DIRECTION IS FIXED per key
 * by `services/broker/listing.py` — `name` ascends, `net` and `turnover`
 * descend. There is no `order` parameter, so a ⇅ header that offers both
 * directions would be lying. See the report.
 */
export const BROKER_LIST_SORTS = ['turnover', 'net', 'name'] as const
export const brokerListSortSchema = z.enum(BROKER_LIST_SORTS)

/** The same, for "every stock this broker traded" (`services/broker/stocks.py`). */
export const BROKER_STOCK_SORTS = ['turnover', 'net', 'trades', 'symbol'] as const
export const brokerStockSortSchema = z.enum(BROKER_STOCK_SORTS)

// ── Shared row shapes ────────────────────────────────────────────────────────

/** Trade counts by size band: small <50 K, medium 50 K–2 L, large 2 L–10 L, block >10 L. */
export const tradeSizeBandsSchema = z.object({
  small: z.number().int().min(0),
  medium: z.number().int().min(0),
  large: z.number().int().min(0),
  block: z.number().int().min(0),
})

/**
 * One stock, from one broker's side of it.
 *
 * The row behind By broker's three tabs, Compare's top stocks and the
 * searchable "every stock it traded" table — six blocks of `BrokerDetail` plus
 * `/brokers/{code}/stocks/` all return this, which is why it is declared once.
 */
export const brokerStockFlowSchema = z.object({
  symbol: z.string().min(1),
  name: z.string().min(1),
  boughtValue: moneySchema,
  soldValue: moneySchema,
  /** Signed: bought minus sold. */
  netValue: moneySchema,
  boughtQty: z.number().int(),
  soldQty: z.number().int(),
  netQty: z.number().int(),
  trades: z.number().int().min(0),
  /** Null when this broker never bought (or never sold) the name in the window. */
  avgBuyRate: moneySchema.nullable(),
  avgSellRate: moneySchema.nullable(),
  lean: brokerLeanSchema,
})

// ── 1. Overview ──────────────────────────────────────────────────────────────

/** A broker and one figure, as the Overview's two ranked lists draw it. */
export const brokerRankRowSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
  value: moneySchema,
})

export const brokerOverviewSchema = z.object({
  window: dataWindowSchema,
  /**
   * ⚠️ A DIFFERENT QUANTITY FROM EVERY OTHER `turnover` IN THIS FILE.
   *
   * This one is the market's real value traded — `buy_turnover` alone,
   * EACH TRADE COUNTED ONCE (`services/broker/overview.py:66`, whose own
   * comment reads `# == total value bought == real market value`, and
   * `types.py:184-187`, which documents the split deliberately).
   *
   * Every PER-BROKER turnover — `BrokerListRow.turnover`, `BrokerKpis.turnover`,
   * `CompareBroker.turnover`, `BrokerPeer.turnover` — is `buy + sell`, BOTH
   * LEGS (`listing.py:31`, `detail.py:85`, `compare.py:38`,
   * `repository.py:137`). So summing the All-brokers "Turnover" column gives
   * roughly twice this tile for the same period, and that is correct, not a
   * bug: they are two different measures wearing one word.
   *
   * The screens say which is which in copy — Overview's tile is captioned
   * "Market turnover / Value of shares traded", the per-broker ones name
   * bought and sold. Do not "reconcile" the numbers.
   */
  turnover: moneySchema,
  trades: z.number().int().min(0),
  companies: z.number().int().min(0),
  brokersTrading: z.number().int().min(0),
  /** How many brokers exist, so the tile can read "41 of 52". */
  ofBrokers: z.number().int().min(0),
  /**
   * Collapsed from the wire's three parallel `busiest_*` fields, which are
   * null together. One nullable object cannot be half-read.
   */
  busiest: z
    .object({
      code: z.string().min(1),
      name: z.string().min(1),
      /** Bought + sold — the PER-BROKER convention, unlike the tile above it
       *  (`overview.py:88`). The strip captions it "bought and sold" for
       *  exactly that reason. */
      turnover: moneySchema,
    })
    .nullable(),
  topBought: z.array(brokerRankRowSchema),
  topSold: z.array(brokerRankRowSchema),
})

/** One stock in the Overview's "Stocks traded most" ranking. */
export const tradedStockSchema = z.object({
  symbol: z.string().min(1),
  /** Null when mm-be has no company name on file — it sends both `null` and `""`. */
  name: z.string().min(1).nullable(),
  /**
   * The BUY side alone, each trade counted once — the same quantity as
   * `brokerOverviewSchema.turnover`, not a per-broker bought + sold
   * (`services/broker/tradedmost.py`'s own docstring).
   */
  valueTraded: moneySchema,
  /** `valueTraded` as a share of the market's, 0–100. Null when nothing traded. */
  sharePct: moneySchema.nullable(),
  trades: z.number().int().min(0),
  brokers: z.number().int().min(0),
})

/**
 * `GET /api/v5/brokers/stocks-traded-most/` — ONE turnover ranking, not the
 * "Bought most" / "Sold most" split the frame draws: mm-be defines no such
 * split (`CHANGES_VS_LEGACY.md` §54, §101). Precomputed-only, like sector
 * flow, so `NO_FLOORSHEET_DATA` means "not summarised yet".
 */
export const stocksTradedMostSchema = z.object({
  window: dataWindowSchema,
  marketValueTraded: moneySchema,
  stocks: z.array(tradedStockSchema),
})

// ── 2. All brokers ───────────────────────────────────────────────────────────

/**
 * `rating` is the live review average, the same number a broker's own page, Compare and the
 * reviews page serve (`listing.py::_ratings` → `rating.to_display`), one decimal to the member and
 * `null` for a house nobody has rated — never 0. It used to be left off: until mm-be's 2026-10
 * change this endpoint served the stale, truncating `broker.avg_rating` integer (an unrated house
 * read `4.00` here and "Not rated yet" on its own page), and a `BrokerListRow.rating` that existed
 * was a field the next screen would render.
 *
 * `turnover` here is BOTH LEGS — bought + sold (`listing.py:31`) — which is
 * not the convention `brokerOverviewSchema.turnover` uses. See that field.
 */
export const brokerListRowSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
  /** Out of 5, or null when no one has reviewed the house yet. */
  rating: moneySchema.nullable(),
  /** Bought + sold. */
  turnover: moneySchema,
  /** Signed: bought minus sold. */
  netTurnover: moneySchema,
  lean: brokerLeanSchema,
})

// ── 3. By broker ─────────────────────────────────────────────────────────────

export const brokerHeaderSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
  logoUrl: z.string().min(1).nullable(),
  rating: moneySchema.nullable(),
  reviewCount: z.number().int().min(0).nullable(),
})

/**
 * `marketSharePct` is null for a multi-session period on purpose:
 * `services/broker/period.py` only ranks a single session, and a share averaged
 * over a month would be a number nobody could reproduce.
 */
export const brokerKpisSchema = z.object({
  turnover: moneySchema,
  trades: z.number().int().min(0),
  marketSharePct: moneySchema.nullable(),
  buySellRatio: moneySchema.nullable(),
})

export const brokerFlowSchema = z.object({
  bought: moneySchema,
  sold: moneySchema,
  /** Signed: bought minus sold. */
  net: moneySchema,
  /** The two halves of the bought/sold bar. Null together when nothing traded. */
  boughtPct: moneySchema.nullable(),
  soldPct: moneySchema.nullable(),
})

export const brokerVsMarketSchema = z.object({
  turnoverMultiple: moneySchema.nullable(),
  tradesMultiple: moneySchema.nullable(),
  buySellRatio: moneySchema.nullable(),
  marketAvgRatio: moneySchema.nullable(),
  avgTrade: moneySchema.nullable(),
  marketAvgTrade: moneySchema.nullable(),
})

export const brokerStyleSchema = z.object({
  /** e.g. "Large-order buyer". Null when the backend could not classify it — not a blank label. */
  styleLabel: z.string().min(1).nullable(),
  /** Share of this broker's trades that were 2 L or larger. */
  bigTradePct: moneySchema.nullable(),
  busiestSector: z.string().min(1).nullable(),
  stocksOnlyBought: z.number().int().min(0),
  stocksTraded: z.number().int().min(0),
  sharesBought: z.number().int().min(0),
  sharesSold: z.number().int().min(0),
  netShares: z.number().int(),
  mostTradedStock: z.string().min(1).nullable(),
})

/**
 * The whole By broker page in one payload.
 *
 * The three tabs — Overview, Activity, Specialty — are CLIENT-SIDE toggles over
 * the six blocks below, not three requests (`services/broker/types.py:122`).
 * That is why there is one `detail` query key and not three: the tabs cannot
 * disagree about the window because they never fetched separately.
 */
export const brokerDetailSchema = z.object({
  window: dataWindowSchema,
  header: brokerHeaderSchema,
  kpis: brokerKpisSchema,
  flow: brokerFlowSchema,
  tradeSizes: tradeSizeBandsSchema,
  vsMarket: brokerVsMarketSchema,
  style: brokerStyleSchema,
  /**
   * The page-level three-card row (`BrokerStockTables`) — Bought most / Sold
   * most / Biggest net. It sits BELOW the tabbed performance card and is drawn
   * on all three tabs, not inside Overview: the tab toggle changes the card
   * above it and nothing here.
   */
  topBought: z.array(brokerStockFlowSchema),
  topSold: z.array(brokerStockFlowSchema),
  topNet: z.array(brokerStockFlowSchema),
  /** Activity tab — the one block that really is tab-scoped (`PerformanceActivity`). */
  activityTop: z.array(brokerStockFlowSchema),
  /**
   * The page-level net-holdings card (`BrokerHoldingsCard`), also drawn on all
   * three tabs. "Shares held" and "Sold more than bought" are two halves of
   * one card, which is why they are a pair.
   */
  sharesHeld: z.array(brokerStockFlowSchema),
  moreSold: z.array(brokerStockFlowSchema),
})

/** The ranked strip above/below the broker being viewed. `isCurrent` marks it. */
export const brokerPeerSchema = z.object({
  rank: z.number().int().min(1),
  code: z.string().min(1),
  name: z.string().min(1),
  turnover: moneySchema,
  marketSharePct: moneySchema.nullable(),
  buySellRatio: moneySchema.nullable(),
  isCurrent: z.boolean(),
})

/** A raw contract this broker was a party to, from its own side. */
export const brokerTradeSchema = z.object({
  contractNo: z.string().min(1),
  symbol: z.string().min(1),
  side: brokerTradeSideSchema,
  /** The broker on the other side. Null when NEPSE did not publish it. */
  counterparty: z.string().min(1).nullable(),
  quantity: z.number().int(),
  rate: moneySchema,
  amount: moneySchema,
  /** ISO timestamp with the Kathmandu offset; null on a contract with no stamp. */
  tradeTime: z.string().min(1).nullable(),
})

// ── Reviews (the one write path) ─────────────────────────────────────────────

export const starBucketSchema = z.object({
  star: z.number().int().min(1).max(5),
  count: z.number().int().min(0),
  /** Null when there are no reviews at all — 0% and "no data" are different. */
  pct: moneySchema.nullable(),
})

export const brokerReviewSchema = z.object({
  id: z.number().int(),
  rating: z.number().int().min(1).max(5),
  text: z.string(),
  /** Already "First L." or "MoneyMitra user". Never a phone or an email. */
  author: z.string().min(1),
  initials: z.string().min(1),
  verified: z.boolean(),
  createdOn: z.string().min(1),
  /**
   * Whether the signed-in member wrote this one. The wire does not send it yet
   * (`asks/broker-reviews.md`), so it reads as false when missing; the card's gold ring and "(you)"
   * stay behind `BROKER_REVIEW_MINE_AVAILABLE`.
   */
  mine: z.boolean().catch(false),
})

/**
 * Summary + one page of reviews.
 *
 * Paging lives INSIDE the payload rather than in `meta`, because the average
 * and the histogram describe every review while `reviews` is one page of them.
 * `total` counts reviews, not pages.
 */
export const brokerReviewsSchema = z.object({
  code: z.string().min(1),
  /** Out of 5, one decimal. Null until the first review. */
  average: moneySchema.nullable(),
  total: z.number().int().min(0),
  /** Always five buckets, 5★ down to 1★, zero-filled. */
  histogram: z.array(starBucketSchema),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  totalPages: z.number().int().min(0),
  reviews: z.array(brokerReviewSchema),
})

/**
 * What a member may submit.
 *
 * 1–5 only, and the backend agrees (`ReviewCreateSerializer`,
 * min_value=1/max_value=5, max_length=2000). Validated here so a slider bug
 * cannot spend a request to be told 0 is not a rating.
 */
export const REVIEW_MIN_RATING = 1
export const REVIEW_MAX_RATING = 5
export const REVIEW_MAX_LENGTH = 2000

export const reviewDraftSchema = z.object({
  rating: z.number().int().min(REVIEW_MIN_RATING).max(REVIEW_MAX_RATING),
  review: z.string().trim().min(1).max(REVIEW_MAX_LENGTH),
})

// ── 4. By stock ──────────────────────────────────────────────────────────────

/*
 * `trades`, `pctOfSide`, `rating` and `totals` arrived in mm-be 5f4ed8f and
 * 6bbfc88, after Brokers › By stock first shipped on this read. A deploy that
 * lags them must still draw that screen. So each of them reads as null when
 * missing or unreadable, rather than failing a row, and with it the whole
 * response. The screens that print them dash a null or leave the block off.
 */

export const stockBrokerRowSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
  /**
   * This house's trade count in the stock across BOTH sides: the per-broker
   * stats carry one `trades` column, so a house that bought ten times and sold
   * five shows 15 in Most bought too (`services/broker/types.py`,
   * `StockBrokerRow`). A screen that heads the column "Trades" says so.
   */
  trades: z.number().int().min(0).nullable().catch(null),
  shares: z.number().int(),
  amount: moneySchema,
  /**
   * Served directly since the 2026-09-25 schema regen. Null on some rows —
   * `StockBrokerTable`'s own `amount ÷ shares` arithmetic is the fallback.
   */
  avgPrice: moneySchema.nullable(),
  /**
   * This broker's share of the stock's trade COUNT in the window. Not "share of
   * buys": that is `pctOfSide`. The two sit in the same range and are easy to
   * swap, which is why neither is named just `pct`.
   */
  pctOfTrades: moneySchema.nullable(),
  /**
   * Its share of the KITTA on this table's own side: bought for Most bought,
   * sold for Most sold, measured against every house that traded that side and
   * not only the ten listed. This is the figure "Share of buys" means.
   */
  pctOfSide: moneySchema.nullable().catch(null),
  /** Out of 5, the figure By broker and Compare show. Null: nobody has reviewed this house. */
  rating: moneySchema.nullable().catch(null),
})

/**
 * One house's NET in the stock: `net_buyers[]` / `net_sellers[]` on `BrokerByStock`, the ten biggest
 * of each side ranked by bought less sold (`net_shares`, negative for a net seller). Distinct from
 * `stockBrokerRowSchema`, whose `shares` is one side's gross. `rating` is null for every row seen.
 */
export const stockNetRowSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
  boughtShares: z.number().int(),
  soldShares: z.number().int(),
  netShares: z.number().int(),
  netAmount: moneySchema,
  rating: moneySchema.nullable().catch(null),
})

/**
 * A broker still sitting on a position in this stock after the window's
 * trades — `left_holding[]` on `BrokerByStock`, added alongside `avg_price`
 * in the same schema regen. Distinct from `mostBought`/`mostSold`: those are
 * trade flow over the window, this is a snapshot. `avgSellPrice` is null for
 * a broker that never sold this session (still fully holding), not zero.
 */
export const stockHolderRowSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
  shares: z.number().int(),
  amount: moneySchema,
  avgBuyPrice: moneySchema.nullable(),
  avgSellPrice: moneySchema.nullable(),
})

/**
 * The whole session for the stock, not just the ten rows each table lists —
 * "Who traded NABIL today". A symbol with no trades sends zeros here, not a 404.
 */
export const stockFlowTotalsSchema = z.object({
  boughtShares: z.number().int().min(0),
  soldShares: z.number().int().min(0),
  boughtAmount: moneySchema,
  soldAmount: moneySchema,
  /**
   * Every house that touched the stock on either side. More than the rows
   * shown: a broker can sit in both tables, or in neither when it falls outside
   * the top ten.
   */
  brokers: z.number().int().min(0),
})

/**
 * By stock alone takes a custom `start`/`end`; mm-be then reports the window's
 * `period` as `custom` (`services/broker/bystock.py`, `get_by_stock`) and
 * `from_date`/`to_date` carry the days it really covered. Every other windowed
 * payload keeps the four-value enum, so the extra value stays local to this one.
 */
export const byStockWindowSchema = dataWindowSchema.extend({
  period: z.union([brokerPeriodSchema, z.literal('custom')]),
  /** First and last session the figures cover; null for a window with no sessions in it. */
  fromDate: z.string().min(1).nullable().optional(),
  toDate: z.string().min(1).nullable().optional(),
})

export const brokerByStockSchema = z.object({
  window: byStockWindowSchema,
  symbol: z.string().min(1),
  name: z.string().min(1),
  ltp: moneySchema.nullable(),
  change: moneySchema.nullable(),
  changePercent: moneySchema.nullable(),
  /** Null when the response carries no readable totals; "Who traded" is then left off. */
  totals: stockFlowTotalsSchema.nullable().catch(null),
  mostBought: z.array(stockBrokerRowSchema),
  mostSold: z.array(stockBrokerRowSchema),
  leftHolding: z.array(stockHolderRowSchema),
  /**
   * Bought less sold, ten a side. Read row by row and empty when absent: a deploy before the net
   * lists must still draw By stock, and one bad row must not blank the rail's two flow blocks.
   */
  netBuyers: lenientList(stockNetRowSchema, 'net buyer').catch([]),
  netSellers: lenientList(stockNetRowSchema, 'net seller').catch([]),
})

// ── 5. Most traded (busy names) ──────────────────────────────────────────────

/** A stock that churned more than its size would suggest. */
export const busyNameSchema = z.object({
  symbol: z.string().min(1),
  name: z.string().min(1),
  /** Turnover as a share of the company's size — the "busy" measure itself. */
  pctOfSize: moneySchema.nullable(),
  changePercent: moneySchema.nullable(),
  ltp: moneySchema.nullable(),
  /**
   * Trades and turnover in the window: the mock's two columns. Not on the wire yet
   * (`asks/most-traded.md`), so `mapBusyName` leaves them out and the cells say so.
   */
  trades: z.number().int().min(0).nullable().optional(),
  turnover: moneySchema.nullable().optional(),
})

// ── 6. Sector flow ───────────────────────────────────────────────────────────

export const sectorFlowRowSchema = z.object({
  sector: z.string().min(1),
  /** Signed: bought minus sold across every broker in the sector. */
  net: moneySchema,
  bought: moneySchema,
  sold: moneySchema,
  brokers: z.number().int().min(0),
  stocks: z.number().int().min(0),
  buyPressurePct: moneySchema.nullable(),
})

/**
 * Sector flow is PRECOMPUTED-ONLY — there is no live fallback
 * (`services/broker/sectorflow.py` hard-codes `source="precomputed"`). It can
 * therefore be empty on a day when every other broker endpoint has data, and
 * an empty `sectors` here is "not summarised yet", not "no money moved".
 */
export const sectorFlowSchema = z.object({
  window: dataWindowSchema,
  /** Collapsed from the wire's paired `most_in_sector` / `most_in_net`. Null together. */
  mostIn: z.object({ sector: z.string().min(1), net: moneySchema }).nullable(),
  mostOut: z.object({ sector: z.string().min(1), net: moneySchema }).nullable(),
  /**
   * How far money moved between sectors, 0 to 100: the strip's "38 of 100". Not on the wire yet
   * (`asks/sector-flow.md`), so `mapSectorFlow` leaves it out and the cell says so.
   */
  rotation: z.number().min(0).max(100).nullable().optional(),
  sectors: z.array(sectorFlowRowSchema),
})

export const sectorBrokerRowSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
  net: moneySchema,
  bought: moneySchema,
  sold: moneySchema,
})

export const sectorBrokersSchema = z.object({
  window: dataWindowSchema,
  sector: z.string().min(1),
  netBuyers: z.number().int().min(0),
  totalBrokers: z.number().int().min(0),
  brokers: z.array(sectorBrokerRowSchema),
})

/** One broker's net money in one sector: a cell of the Broker-by-sector grid. */
export const brokerSectorCellSchema = z.object({
  sector: z.string().min(1),
  /**
   * Signed: bought minus sold. NULL when this broker did not trade the sector
   * at all, which is not zero. "Didn't touch it" and "traded it and came out
   * level" are different facts (`CHANGES_VS_LEGACY.md` §102), so nothing may
   * fold a null into `0`.
   */
  net: moneySchema.nullable(),
})

export const brokerSectorRowSchema = z.object({
  code: z.string().min(1),
  /** The trading house, via `brokerDisplayName`: never blank. */
  name: z.string().min(1),
  /** Bought + sold across every sector: the rank the rows are sorted on. */
  tradedValue: moneySchema,
  /** This broker's own net across every sector, not a sum of the cells shown. */
  net: moneySchema,
  sectorsTraded: z.number().int().min(0),
  cells: z.array(brokerSectorCellSchema),
})

/**
 * `GET /api/v5/brokers/by-sector/`: brokers down the side, sectors across the
 * top, one request (`services/broker/bysector.py`). `sectors` is the column
 * order, busiest first, and holds only sectors that traded. Rows are ranked
 * by money traded.
 *
 * The grid is rectangular by contract: every row carries one cell per column,
 * in column order. That is asserted rather than assumed, because a card reads
 * the cells by position, and a row that drifted by one would print each figure
 * under the wrong sector's heading. A broken promise fails the parse, loudly,
 * instead. Precomputed-only, like sector flow, so `NO_FLOORSHEET_DATA` means
 * "not summarised yet".
 */
export const brokersBySectorSchema = z
  .object({
    window: dataWindowSchema,
    sectors: z.array(z.string().min(1)),
    brokers: z.array(brokerSectorRowSchema),
  })
  .refine(
    (grid) =>
      grid.brokers.every(
        (row) =>
          row.cells.length === grid.sectors.length &&
          row.cells.every((cell, index) => cell.sector === grid.sectors[index]),
      ),
    'Every broker row must carry one cell per sector, in the order of `sectors`.',
  )

// ── 7. Large trades ──────────────────────────────────────────────────────────

export const largeTradeSchema = z.object({
  contractNo: z.string().min(1),
  symbol: z.string().min(1),
  band: largeTradeBandSchema,
  /** Either side may be unpublished, so each is one nullable object rather than two loose fields. */
  buyer: z.object({ code: z.string().min(1), name: z.string().min(1) }).nullable(),
  seller: z.object({ code: z.string().min(1), name: z.string().min(1) }).nullable(),
  shares: z.number().int(),
  /** Price per share, for the row card. */
  rate: moneySchema,
  amount: moneySchema,
  tradeTime: z.string().min(1).nullable(),
})

/**
 * Tiles and one page of trades in a single payload.
 *
 * NOTE: no `period` and no `source` — large trades are a today-only read, so
 * there is no `window` to carry and no partial-window caption to draw.
 */
export const largeTradesSchema = z.object({
  asOf: z.string().min(1).nullable(),
  size: largeTradeSizeSchema,
  /** Trades at or above the floor today. */
  count: z.number().int().min(0),
  totalValue: moneySchema,
  veryLarge: z.number().int().min(0),
  large: z.number().int().min(0),
  avgSize: moneySchema.nullable(),
  /** The busiest buyer/seller pair of the day. Null when no pair repeated. */
  topPair: z
    .object({
      buyer: z.string().min(1),
      seller: z.string().min(1),
      count: z.number().int().min(0),
    })
    .nullable(),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  total: z.number().int().min(0),
  totalPages: z.number().int().min(0),
  trades: z.array(largeTradeSchema),
})

export const largeTradeLeaderSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
  trades: z.number().int().min(0),
  value: moneySchema,
  lean: largeTradeLeanSchema,
})

// ── 8. Compare ───────────────────────────────────────────────────────────────

export const COMPARE_MIN_BROKERS = 2
export const COMPARE_MAX_BROKERS = 4

export const compareBrokerSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
  rating: moneySchema.nullable(),
  turnover: moneySchema,
  netFlow: moneySchema,
  trades: z.number().int().min(0),
  avgTrade: moneySchema.nullable(),
  marketSharePct: moneySchema.nullable(),
  buySellRatio: moneySchema.nullable(),
  stocksTraded: z.number().int().min(0),
  tradeSizes: tradeSizeBandsSchema,
})

/**
 * Who wins each row — BROKER CODES, not values, and null when nothing wins
 * (every compared broker tied, or none had the figure at all).
 *
 * Deliberately not `Money`: "58" here is an identifier that happens to look
 * numeric, which is the whole reason broker codes stay strings.
 */
export const compareBestSchema = z.object({
  turnover: z.string().min(1).nullable(),
  netFlow: z.string().min(1).nullable(),
  trades: z.string().min(1).nullable(),
  avgTrade: z.string().min(1).nullable(),
  marketShare: z.string().min(1).nullable(),
  buySellRatio: z.string().min(1).nullable(),
  stocks: z.string().min(1).nullable(),
  rating: z.string().min(1).nullable(),
})

export const compareSchema = z.object({
  window: dataWindowSchema,
  brokers: z.array(compareBrokerSchema),
  best: compareBestSchema,
})

export const stocksInCommonSchema = z.object({
  window: dataWindowSchema,
  /** Echoed back in the order they were asked for, so columns keep their places. */
  brokers: z.array(z.string().min(1)),
  count: z.number().int().min(0),
  stocks: z.array(z.object({ symbol: z.string().min(1), name: z.string().min(1) })),
})

// ── 9. Large holdings ────────────────────────────────────────────────────────

/**
 * One broker's net position in one stock, as a share of the company itself.
 *
 * `pctOfCapital` is `net_qty / company.listed_shares * 100`
 * (`services/broker/largeholdings.py::_pct`) and is NULL when the backend has
 * no share count on file for the name — those rows are ranked last rather than
 * dropped, so the table has to render the gap as a gap. Legacy divided a kitta
 * count by a rupee capital figure and never multiplied by 100; nothing on this
 * side recomputes the percentage, so that cannot recur here.
 *
 * `netQty` is strictly positive: "holdings" means net buyers building a
 * position, and the service drops every non-positive net before it ranks
 * (`if net <= 0: continue`). Asserted rather than assumed — a zero or negative
 * net arriving here would draw a negative bar under a heading that says
 * "holding", and this feature's rule is that loud beats silently wrong.
 */
export const largeHoldingSchema = z.object({
  symbol: z.string().min(1),
  name: z.string().min(1),
  code: z.string().min(1),
  /** The trading house, via `brokerDisplayName` — never blank. */
  brokerName: z.string().min(1),
  netQty: z.number().int().min(1),
  /** Buy turnover ÷ buy quantity. Null when the broker only sold in the window. */
  avgRate: moneySchema.nullable(),
  pctOfCapital: moneySchema.nullable(),
})

/**
 * Tiles-free, but paged INSIDE the payload — the same shape as `LargeTrades`
 * and for the same reason: `LargeHoldingsView` returns a plain `Response`, not
 * a `ListResponse`, so the page numbers travel in `data` and there is no
 * envelope `meta` to read them out of. It is therefore NOT a `TablePage`, and
 * `serverPage`/`pageTotal` play no part in it.
 *
 * `sector` is echoed back so a narrowed table can say what it was narrowed to.
 * Precomputed-only, like sector flow — `largeholdings.py` hard-codes
 * `source="precomputed"` and gates its window on `broker_symbol_stats` — so an
 * empty `holdings` is "not summarised yet", not "no broker holds anything".
 */
export const largeHoldingsSchema = z.object({
  window: dataWindowSchema,
  sector: z.string().min(1).nullable(),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  total: z.number().int().min(0),
  totalPages: z.number().int().min(0),
  holdings: z.array(largeHoldingSchema),
})

// ── 10. PV range ─────────────────────────────────────────────────────────────

/**
 * One Rs 1 price bucket and the kitta that changed hands inside it.
 *
 * `[low, high)` with `high === low + 1` today (`pvrange.py` builds
 * `PVBucket(low=bucket, high=bucket + 1)`), but the width is READ rather than
 * assumed anywhere it is printed — see `bucketWidth` in the screen's own
 * format module. A backend that widens the bucket for a four-figure share
 * price should change the caption, not silently relabel Rs 10 of range as
 * "Rs 1 wide".
 */
export const pvBucketSchema = z.object({
  low: z.number().int().min(0),
  high: z.number().int().min(0),
  quantity: z.number().int().min(0),
})

/**
 * Where one stock's volume actually traded, bucketed by price.
 *
 * `busiest` collapses the wire's three parallel `busiest_*` fields, the same
 * way `mapOverview` collapses the busiest broker: the bucket is what makes the
 * range exist, so a `busiest_high` missing beside a present `busiest_low` is a
 * broken promise and throws (`required`), while `pct` stays nullable on its own
 * because `pvrange.py` legitimately leaves it `None` when the window's total
 * quantity is zero.
 *
 * THE BUSIEST RANGE IS ONE BUCKET WIDE — `max(buckets, key=quantity)`, not a
 * merged run of adjacent heavy buckets. V2's frame (`4243:104586`) draws "306
 * to 308" against 79% of the day, which is two buckets summed; the backend
 * sends the single Rs 1 bucket and its own share (~42% on that data). Nothing
 * here merges buckets to reach the frame's number: that would be inventing a
 * measure the backend did not compute, and the tile would then disagree with
 * the bar the member is looking at. See the report.
 */
export const pvRangeSchema = z.object({
  window: dataWindowSchema,
  symbol: z.string().min(1),
  name: z.string().min(1),
  /** The broker codes the profile was narrowed to, echoed back. Null means "any". */
  buyer: z.string().min(1).nullable(),
  seller: z.string().min(1).nullable(),
  totalTraded: z.number().int().min(0),
  busiest: z
    .object({
      low: z.number().int().min(0),
      high: z.number().int().min(0),
      /** Its share of the window's total quantity. Null when nothing traded. */
      pct: moneySchema.nullable(),
    })
    .nullable(),
  buckets: z.array(pvBucketSchema),
})

// ── What the URL carries ─────────────────────────────────────────────────────

/**
 * Offset paging, 1-based, over the closed `PAGE_SIZES` — the same contract as
 * every other table in the app (src/schemas/table.ts). These endpoints page
 * SERVER-side, so the page is a request parameter rather than a slice taken
 * afterwards; `page` is still the number on the button.
 */
const pagedQuery = {
  page: z.number().int().min(1).default(1),
  pageSize: pageSizeSchema.default(20),
}

/**
 * Just the paging pair, for a surface whose only query IS the paging.
 *
 * Exported so a route search schema can COMPOSE the app's paging contract
 * instead of restating `page`/`pageSize` — `compareSearchSchema`
 * (src/screens/brokers/search.ts) extends this for the stocks-in-common
 * table, which pages a list the client already holds and so has no endpoint
 * query of its own to borrow. Restating the pair there would be the exact
 * duplication this file's reconciliation removed once already.
 */
export const pagedQuerySchema = z.object(pagedQuery)

export const brokerListQuerySchema = z.object({
  ...pagedQuery,
  /**
   * All brokers is the one paged broker surface with a live route default to answer to
   * (`src/screens/brokers/search.ts`, `allBrokersSearchSchema`, which this schema now IS). The
   * revamp mock opens it on 20 rows a page (br:ls, 2026-10-28), the same as `pagedQuery`'s own.
   */
  pageSize: pageSizeSchema.default(20),
  period: brokerPeriodSchema.default('today'),
  /** Free-text search over code and name. Absent means "every broker". */
  q: codeParamSchema.optional(),
  sort: brokerListSortSchema.default('turnover'),
})

export const brokerStocksQuerySchema = z.object({
  ...pagedQuery,
  /** By broker's "Every stock" opens on ten rows, as the mock's does (ruling 8, 2026-10-28). */
  pageSize: pageSizeSchema.default(10),
  period: brokerPeriodSchema.default('today'),
  q: codeParamSchema.optional(),
  sort: brokerStockSortSchema.default('turnover'),
})

/** Paging and nothing else — the raw contracts list takes no other filter. */
export const brokerTradesQuerySchema = pagedQuerySchema

/** The same: a review is not a windowed measure, so there is no period here. */
export const brokerReviewsQuerySchema = pagedQuerySchema

export const busyNamesQuerySchema = z.object({
  ...pagedQuery,
  /** The mock's Most traded shows ten: its default, over the paged family's 20. */
  pageSize: pageSizeSchema.default(10),
  period: brokerPeriodSchema.default('today'),
  /** Narrow to one sector. Absent means "All sectors". */
  sector: z.string().min(1).optional(),
})

/**
 * A past session, AD `YYYY-MM-DD`. Omitted is the latest settled one. mm-be answers a holiday or
 * an unloaded day with zero counts, and refuses a day after the latest settled one
 * (`SESSION_NOT_SETTLED`).
 */
const sessionDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)

export const largeTradesQuerySchema = z.object({
  ...pagedQuery,
  // The revamp mock opens on 10 rows (br:lh, ruling 8).
  pageSize: pageSizeSchema.default(10),
  size: largeTradeSizeSchema.default('10l'),
  /** Narrow to one listed name. */
  stock: codeParamSchema.optional(),
  /** Narrow to one broker number. A bare `?broker=58` parses as a number. */
  broker: codeParamSchema.optional(),
  date: sessionDateSchema.optional(),
})

export const largeTradeLeadersQuerySchema = z.object({
  size: largeTradeSizeSchema.default('10l'),
  stock: z.string().min(1).optional(),
})

/**
 * Large holdings is the ONE broker query whose period default is not `today`.
 *
 * `LargeHoldingsRequestSerializer` defaults it to `1m`, and V2's frame
 * (`4243:103846`) draws "1 month" as the selected chip — a net position built
 * over a single session is barely a position, so the two agree. Matching the
 * backend here means a bare `/brokers/large-holdings` and a bare
 * `GET /api/v5/brokers/large-holdings/` describe the same window, which is the
 * property `brokerListQuerySchema`'s own comment argues for from the other
 * direction.
 *
 * `sector` is part of the ENDPOINT's query and deliberately NOT part of the
 * route's — see `largeHoldingsSearchSchema` in `src/screens/brokers/search.ts`
 * for why, and `toLargeHoldingsQuery` for the projection between them.
 */
export const largeHoldingsQuerySchema = z.object({
  ...pagedQuery,
  // The revamp mock opens on 10 rows (br:lh, ruling 8).
  pageSize: pageSizeSchema.default(10),
  period: brokerPeriodSchema.default('1m'),
  /** Narrow to one sector. Absent means every sector. */
  sector: z.string().min(1).optional(),
})

/**
 * PV range takes no paging: the profile IS the whole window, bucket for
 * bucket, and a page of a volume profile would be a chart with its middle
 * missing. `symbol` is a path parameter, not a query one, so it is not here
 * either — the service takes it positionally, like `byStock` and `detail`.
 *
 * `buyer` and `seller` are broker codes and stay strings through
 * `codeParamSchema`: a bare `?buyer=58` would otherwise parse as the number 58
 * and lose the leading zero on `?buyer=07`.
 */
export const pvRangeQuerySchema = z.object({
  period: brokerPeriodSchema.default('today'),
  buyer: codeParamSchema.optional(),
  seller: codeParamSchema.optional(),
})

/**
 * Compare takes 2–4 broker codes. The ARRAY is the app's shape; the wire wants
 * them comma-separated, and that join happens once, in the service.
 *
 * Order is meaningful — it is the column order — so it is part of the query and
 * therefore part of the key. Re-ordering the columns is a different question
 * with a different answer, not a cache hit.
 */
export const compareQuerySchema = z.object({
  brokers: z.array(z.string().min(1)).min(COMPARE_MIN_BROKERS).max(COMPARE_MAX_BROKERS),
  period: brokerPeriodSchema.default('today'),
})

/** `"58,07"` → `["58", "07"]`. Blanks dropped; codes stay strings, leading zero and all. */
export const parseBrokerCodes = (value: string): string[] =>
  value
    .split(',')
    .map((code) => code.trim())
    .filter((code) => code.length > 0)

/** The inverse, for the one place the wire needs it. */
export const formatBrokerCodes = (codes: readonly string[]): string => codes.join(',')

/**
 * A name for a broker that always prints.
 *
 * The backend sends `name: null` for a member it has no name on file for, and
 * "Broker 07" is a better cell than an empty one — the number is the
 * identifier a member recognises anyway.
 */
export const brokerDisplayName = (code: string, name: string | null): string =>
  name === null || name.trim().length === 0 ? `Broker ${code}` : name

/**
 * The trailing corporate boilerplate `brokerShortName` strips, one word at a
 * time from the end — normalised without its trailing dot(s), so "Ltd" and
 * "Ltd." both match. "&"/"and" are in the set too, so a connector a strip
 * leaves dangling ("… Securities & Co." → drop "Co." → drop "&") keeps going
 * rather than sitting orphaned on the end.
 */
const CORPORATE_WORDS = new Set([
  'securities',
  'security',
  'stock',
  'broking',
  'brokers',
  'capital',
  'markets',
  'investment',
  'investments',
  'company',
  'co',
  'ltd',
  'limited',
  'pvt',
  'private',
  'and',
  '&',
])

/**
 * A broker's legal name with the trailing corporate boilerplate dropped —
 * "Nabil Securities Company Ltd." → "Nabil" — for a cell too narrow for the
 * full name (Overview's ranked rows and its busiest-broker tile). The wire
 * has no separate short name to read instead (see the Overview build
 * report), so this is a client-side guess: it strips one trailing word at a
 * time, repeatedly, as long as it matches `CORPORATE_WORDS`, but never the
 * FIRST word and never down to nothing — a name that is entirely corporate
 * words ("Capital Markets Ltd.") still returns its first word untouched.
 * Callers keep the FULL name in `title` and the accessible name; this
 * function only ever answers the question "what prints".
 */
export const brokerShortName = (name: string): string => {
  const words = name.trim().split(/\s+/)
  while (words.length > 1) {
    const last = words.pop()
    if (last === undefined) break
    if (!CORPORATE_WORDS.has(last.replace(/\.+$/, '').toLowerCase())) {
      words.push(last)
      break
    }
  }
  return words.join(' ')
}

/**
 * The legal-form words `brokerFullName` drops from the end: "Ltd.", "Pvt.", "Co." and their long
 * forms, plus a connector left dangling by the strip ("… & Co. Ltd."). Normalised without dots.
 */
const LEGAL_FORM_WORDS = new Set(['ltd', 'limited', 'pvt', 'private', 'co', 'company', 'and', '&'])

/**
 * A house's full name as the mock prints it in a header, a picker and a pick card: the legal name
 * without its legal form — "NIBL Ace Capital Ltd." → "NIBL Ace Capital", "Nabil Securities Company
 * Ltd." → "Nabil Securities". Unlike `brokerShortName` it keeps the trade words ("Capital",
 * "Securities"), so it still reads as the firm's name. Never strips the first word, and a missing
 * name falls back to "Broker {code}" as `brokerDisplayName` does.
 */
export const brokerFullName = (code: string, name: string | null): string => {
  const words = brokerDisplayName(code, name).trim().split(/\s+/)
  while (
    words.length > 1 &&
    LEGAL_FORM_WORDS.has((words.at(-1) ?? '').replace(/[.,]+$/, '').toLowerCase())
  ) {
    words.pop()
  }
  return words.join(' ').replace(/,$/, '')
}

/** The same, for a listed name: a stock with no company name shows its symbol. */
export const stockDisplayName = (symbol: string, name: string | null): string =>
  name === null || name.trim().length === 0 ? symbol : name

export type BrokerPeriod = z.infer<typeof brokerPeriodSchema>
export type BrokerSource = z.infer<typeof brokerSourceSchema>
export type DataWindow = z.infer<typeof dataWindowSchema>
export type ByStockWindow = z.infer<typeof byStockWindowSchema>
export type ListWindow = z.infer<typeof listWindowSchema>
export type BrokerLean = z.infer<typeof brokerLeanSchema>
export type LargeTradeLean = z.infer<typeof largeTradeLeanSchema>
export type BrokerTradeSide = z.infer<typeof brokerTradeSideSchema>
export type LargeTradeSize = z.infer<typeof largeTradeSizeSchema>
export type LargeTradeBand = z.infer<typeof largeTradeBandSchema>
export type BrokerListSort = z.infer<typeof brokerListSortSchema>
export type BrokerStockSort = z.infer<typeof brokerStockSortSchema>

export type TradeSizeBands = z.infer<typeof tradeSizeBandsSchema>
export type BrokerStockFlow = z.infer<typeof brokerStockFlowSchema>
export type BrokerRankRow = z.infer<typeof brokerRankRowSchema>
export type BrokerOverview = z.infer<typeof brokerOverviewSchema>
export type TradedStock = z.infer<typeof tradedStockSchema>
export type StocksTradedMost = z.infer<typeof stocksTradedMostSchema>
export type BrokerListRow = z.infer<typeof brokerListRowSchema>
export type BrokerHeader = z.infer<typeof brokerHeaderSchema>
export type BrokerKpis = z.infer<typeof brokerKpisSchema>
export type BrokerFlow = z.infer<typeof brokerFlowSchema>
export type BrokerVsMarket = z.infer<typeof brokerVsMarketSchema>
export type BrokerStyle = z.infer<typeof brokerStyleSchema>
export type BrokerDetail = z.infer<typeof brokerDetailSchema>
export type BrokerPeer = z.infer<typeof brokerPeerSchema>
export type BrokerTrade = z.infer<typeof brokerTradeSchema>
export type StarBucket = z.infer<typeof starBucketSchema>
export type BrokerReview = z.infer<typeof brokerReviewSchema>
export type BrokerReviews = z.infer<typeof brokerReviewsSchema>
export type ReviewDraft = z.infer<typeof reviewDraftSchema>
export type StockBrokerRow = z.infer<typeof stockBrokerRowSchema>
export type StockHolderRow = z.infer<typeof stockHolderRowSchema>
export type StockNetRow = z.infer<typeof stockNetRowSchema>
export type StockFlowTotals = z.infer<typeof stockFlowTotalsSchema>
export type BrokerByStock = z.infer<typeof brokerByStockSchema>

/** An AD `YYYY-MM-DD` span for By stock's custom range; mm-be allows at most a year. */
export interface BrokerDateRange {
  start: string
  end: string
}
export type BusyName = z.infer<typeof busyNameSchema>
export type SectorFlowRow = z.infer<typeof sectorFlowRowSchema>
export type SectorFlow = z.infer<typeof sectorFlowSchema>
export type SectorBrokerRow = z.infer<typeof sectorBrokerRowSchema>
export type SectorBrokers = z.infer<typeof sectorBrokersSchema>
export type BrokerSectorCell = z.infer<typeof brokerSectorCellSchema>
export type BrokerSectorRow = z.infer<typeof brokerSectorRowSchema>
export type BrokersBySector = z.infer<typeof brokersBySectorSchema>
export type LargeTrade = z.infer<typeof largeTradeSchema>
export type LargeTrades = z.infer<typeof largeTradesSchema>
export type LargeTradeLeader = z.infer<typeof largeTradeLeaderSchema>
export type LargeHolding = z.infer<typeof largeHoldingSchema>
export type LargeHoldings = z.infer<typeof largeHoldingsSchema>
export type PVBucket = z.infer<typeof pvBucketSchema>
export type PVRange = z.infer<typeof pvRangeSchema>
export type CompareBroker = z.infer<typeof compareBrokerSchema>
export type CompareBest = z.infer<typeof compareBestSchema>
export type Compare = z.infer<typeof compareSchema>
export type StocksInCommon = z.infer<typeof stocksInCommonSchema>

export type BrokerListQuery = z.infer<typeof brokerListQuerySchema>
export type BrokerStocksQuery = z.infer<typeof brokerStocksQuerySchema>
export type BrokerTradesQuery = z.infer<typeof brokerTradesQuerySchema>
export type BrokerReviewsQuery = z.infer<typeof brokerReviewsQuerySchema>
export type BusyNamesQuery = z.infer<typeof busyNamesQuerySchema>
export type LargeTradesQuery = z.infer<typeof largeTradesQuerySchema>
export type LargeTradeLeadersQuery = z.infer<typeof largeTradeLeadersQuerySchema>
export type LargeHoldingsQuery = z.infer<typeof largeHoldingsQuerySchema>
export type PVRangeQuery = z.infer<typeof pvRangeQuerySchema>
export type CompareQuery = z.infer<typeof compareQuerySchema>
